import React, { Children, Fragment, isValidElement, useMemo, useState } from "react";
import SearchableSelect from "./SearchableSelect";

// ════════════════════════════════════════════════════════════════════════
//  AppSelect — THE dropdown of the whole app (the gold design: a pill with a gold border, and a card that opens under it with a SEARCH BAR on top,
//  the options (each may carry an icon / logo), a gold tick on the chosen one and a soft gold wave at the bottom).
//
//  It is written so that the old MUI code keeps working with almost no change:
//    <AppSelect label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
//      <MenuItem value="">All</MenuItem>
//      <MenuItem value="open">Open</MenuItem>
//    </AppSelect>
//  — the children are <MenuItem value="…">label</MenuItem> (a MenuItem's content may be an icon + text; the text is what the search looks at),
//  and onChange receives an event-like object { target: { value, name } } exactly like <TextField select> / <Select>.
//  With `options={[{ value, label, avatar?, icon? }]}` and onChange(value) use SearchableSelect directly.
//
//  props: label, labelIcon, labelAdornment, value (or defaultValue), onChange, name, placeholder, disabled, error, helperText, required,
//         fullWidth (default true), size ("small" | "tiny"), sx (the outer box: width, minWidth, margin …), searchPlaceholder.
// ════════════════════════════════════════════════════════════════════════

const textOf = (node) => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join(" ");
  if (isValidElement(node)) return textOf(node.props.children);
  return "";
};

/** The <MenuItem value> children → [{ value, label, node?, disabled? }]. */
export function optionsFromChildren(children) {
  const out = [];
  const walk = (nodes) => Children.forEach(nodes, (c) => {
    if (!isValidElement(c)) return;
    if (c.type === Fragment) { walk(c.props.children); return; }
    if (c.props && Object.prototype.hasOwnProperty.call(c.props, "value")) {
      const content = c.props.children;
      const plain = typeof content === "string" || typeof content === "number";
      out.push({ value: c.props.value, label: textOf(content).trim() || String(c.props.value), node: plain ? undefined : content, disabled: !!c.props.disabled });
    } else if (c.props && c.props.children) walk(c.props.children);
  });
  walk(children);
  return out;
}

export default function AppSelect({
  label, labelIcon, labelAdornment, value, defaultValue, onChange, name, children, options, placeholder, disabled, error, helperText, required,
  fullWidth = true, size, sx, searchPlaceholder, loading,
}) {
  const [own, setOwn] = useState(defaultValue === undefined ? "" : defaultValue);
  const controlled = value !== undefined;
  const current = controlled ? value : own;
  const list = useMemo(() => options || optionsFromChildren(children), [options, children]);
  const pick = (v) => {
    if (!controlled) setOwn(v);
    if (onChange) onChange({ target: { value: v, name }, currentTarget: { value: v, name }, preventDefault() {}, stopPropagation() {}, persist() {} }, v);
  };
  return (
    <SearchableSelect variant="gold" label={label} labelIcon={labelIcon} labelAdornment={labelAdornment} value={current === null ? "" : current} onChange={pick} options={list}
      placeholder={placeholder || (label ? `Select ${String(label).toLowerCase()}` : "Select…")} disabled={disabled} error={!!error} helperText={typeof helperText === "string" ? helperText : ""}
      required={required} fullWidth={fullWidth} size={size} sx={sx} searchPlaceholder={searchPlaceholder} loading={loading} />
  );
}
